import React from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Button } from "../../../../shared/components/ui/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { vm } from "../../../../shared/utils/validationMessages";

// ✅ Validation schema
const formSchema = z.object({
  name: z.string().min(1, { message: vm.NAME_REQUIRED }),
  city_id: z.string().min(1, { message: vm.CITY_REQUIRED }),
});

export type CreateBuildingFormValues = z.infer<typeof formSchema>;

interface CreateBuildingFormProps {
  defaultValues: CreateBuildingFormValues;
  onSubmit: (data: CreateBuildingFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  cityOptions: { value: string; label: string }[];
}

export const CreateBuildingForm: React.FC<CreateBuildingFormProps> = ({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  cityOptions,
}) => {
  const form = useForm<CreateBuildingFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-5 w-full max-w-md mx-auto"
    >
      {/* Nombre */}
      <FormField
        type="input"
        label="Nombre"
        required
        inputProps={{
          type: "text",
          placeholder: "Nombre de la unidad residencial",
          ...register("name"),
        }}
        error={errors.name?.message}
      />

      {/* Ciudad */}
      <FormField
        type="searchableSelect"
        label="Ciudad"
        required
        searchableSelectProps={{
          options: [{ value: "", label: "Seleccionar ciudad" }, ...cityOptions],
          value: form.watch("city_id") || "",
          onChange: (value) => form.setValue("city_id", value, { shouldValidate: true }),
          onBlur: register("city_id").onBlur,
          name: register("city_id").name,
          placeholder: "Buscar ciudad...",
        }}
        error={errors.city_id?.message}
      />

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-2">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-sm px-4"
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="text-sm px-4"
        >
          {isSubmitting ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </form>
  );
};

